/**
 * Mortgage amortization helpers (Patch BL).
 *
 * Compute-on-render: given a user-entered snapshot (outstanding balance
 * at a known date, monthly payment, APR), roll forward month by month to
 * any target date. No stored payment schedule; the math is cheap (max
 * ~360 iterations for a 30-year mortgage).
 *
 * Why not store a schedule: the user's inputs can shift (extra payment,
 * refi rate change, new balance from a statement). Recomputing at render
 * time means every UI displays the current truth without sync gymnastics.
 *
 * Pattern matches computeLoanState in src/engine/loan-state.ts, which
 * does the same compute-on-render thing for the Bitcoin loan based on
 * borrow/repay events. Different math (event ledger vs amortization
 * schedule) but the same "no stored derived state" principle.
 */

import { Decimal } from './money.js';
import type { Mortgage } from './types.js';

/**
 * Months elapsed between two dates, calendar-aware (not 30-day blocks).
 * (Y2 - Y1) * 12 + (M2 - M1) gives the count of complete months passed.
 * Negative result clamps to 0 since "future asOf" makes no sense for
 * a backwards-looking projection.
 */
function monthsBetween(start: Date, end: Date): number {
  const months = (end.getFullYear() - start.getFullYear()) * 12
    + (end.getMonth() - start.getMonth());
  return Math.max(0, months);
}

/**
 * Roll the mortgage's stored balance forward to the target date using
 * monthly amortization. Returns current outstanding principal in USD.
 *
 * Per-month step:
 *   interest = balance * (apr / 12)
 *   principal = monthlyPayment - interest
 *   newBalance = balance - principal, clamped to zero
 *
 * Edge cases:
 *   - Target date before balanceAsOfDate: returns the stored balance
 *     unchanged (no negative amortization in time).
 *   - monthlyPayment <= monthly interest: balance grows (negative
 *     amortization). Returned as-is so the UI can warn. We don't
 *     simulate this indefinitely beyond the entered timeline.
 *   - Balance reaches zero: we stop and return zero rather than letting
 *     it go negative. A real mortgage's final payment is smaller than
 *     the standard monthlyPayment; we don't model that micro-detail.
 */
export function currentMortgageBalance(m: Mortgage, asOf: Date): Decimal {
  const monthlyRate = m.aprPct.div(12);
  const months = monthsBetween(m.balanceAsOfDate, asOf);
  let balance = m.outstandingBalanceUsd;
  for (let i = 0; i < months; i++) {
    const interest = balance.times(monthlyRate);
    const principal = m.monthlyPaymentUsd.minus(interest);
    balance = balance.minus(principal);
    if (balance.lessThanOrEqualTo(0)) {
      return new Decimal(0);
    }
  }
  return balance;
}

/**
 * Months remaining until the mortgage pays off given the current balance
 * and payment terms. Uses the standard annuity formula:
 *
 *   N = log(M / (M - P*r)) / log(1 + r)
 *
 * where M = monthly payment, P = current balance, r = monthly rate.
 *
 * Returns null when the math is undefined or non-finite (e.g., payment
 * <= interest, in which case the loan never pays off).
 */
export function impliedMonthsToPayoff(
  balanceUsd: Decimal,
  monthlyPaymentUsd: Decimal,
  aprPct: Decimal,
): number | null {
  const r = aprPct.div(12);
  const P = balanceUsd;
  const M = monthlyPaymentUsd;
  // payment must cover at least the monthly interest, otherwise the
  // loan never amortizes
  const monthlyInterest = P.times(r);
  if (M.lessThanOrEqualTo(monthlyInterest)) {
    return null;
  }
  // log(M / (M - P*r)) / log(1 + r)
  const numerator = Math.log(M.toNumber() / (M.minus(P.times(r))).toNumber());
  const denominator = Math.log(1 + r.toNumber());
  const n = numerator / denominator;
  if (!isFinite(n) || n < 0) return null;
  return n;
}

/**
 * Add `months` months to a date, returning a new Date. Used to compute
 * the implied end date for the consistency-check hint on the mortgage
 * form: given the user's balance, payment, and APR, where SHOULD the
 * end date be?
 */
export function addMonths(d: Date, months: number): Date {
  const result = new Date(d);
  result.setMonth(result.getMonth() + months);
  return result;
}
